import {
  FormFieldHelpTextVisibilityContext,
  FormFieldLabelVisibilityContext,
  useFormFieldHelpTextVisibility,
  useFormFieldLabelVisibility
} from '@Pages/pageRuntime/components/ui/form/formFieldVisibility';
import { Label } from '@Pages/pageRuntime/components/ui/form/Label';
import { cn } from '@Pages/pageRuntime/lib/utils';
import React, { useId } from 'react';

export interface FormFieldProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange' | 'onSelect'
> {
  label: React.ReactNode;
  labelActions?: React.ReactNode;
  labelPlacement?: 'stacked' | 'inline';
  id?: string;
  helpText?: React.ReactNode;
  helpTextPlacement?: 'before-control' | 'after-control';
  error?: string | string[];
  children?: React.ReactNode;
  isRequired?: boolean | null;
  inputActions?: React.ReactNode;
  classes?: string;
}

export type FormFieldPropsWithoutChildren = Omit<FormFieldProps, 'children'>;

export const FormField = ({
  label,
  labelActions,
  id,
  classes,
  helpText,
  helpTextPlacement = 'after-control',
  error,
  children,
  inputActions,
  isRequired = false,
  labelPlacement = 'stacked',
  ref,
  ...props
}: FormFieldProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const generatedId = useId();
  // A surrounding layout can own the naming (a property row with its own label
  // column); the label then exists for assistive technology only.
  const isLabelVisuallyHidden = useFormFieldLabelVisibility() === 'hidden';
  const isHelpTextVisuallyHidden = useFormFieldHelpTextVisibility() === 'hidden';
  const isInline = labelPlacement === 'inline';
  const fieldId = id ?? generatedId;
  // Inline fields keep the control in the first column and the label in the second, so input
  // actions need a trailing column of their own instead of sharing the narrow control column.
  const inlineActions = isInline ? inputActions : null;
  const stackedActions = isInline ? null : inputActions;
  const labelId = label ? `${fieldId}-label` : undefined;
  const helpTextId = helpText ? `${fieldId}-help` : undefined;
  const errors = error ? (Array.isArray(error) ? error : [error]) : [];
  const errorId = errors.length ? `${fieldId}-error` : undefined;
  const describedBy = [helpTextId, errorId].filter(Boolean).join(' ') || undefined;
  const clonedChildren = children
    ? React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) {
          return child;
        }

        const childProps = child.props as Record<string, unknown>;
        const described =
          [childProps?.['aria-describedby'], describedBy].filter(Boolean).join(' ') || undefined;
        // aria-labelledby outranks aria-label in accessible-name computation,
        // so only wire the field label to children that don't carry their own
        // explicit label — a child's aria-label is deliberate (often state-
        // aware) and force-appending the field label would mask it.
        const labelledBy =
          childProps?.['aria-label'] && !childProps?.['aria-labelledby']
            ? undefined
            : [childProps?.['aria-labelledby'], labelId].filter(Boolean).join(' ') || undefined;

        return React.cloneElement(
          child as React.ReactElement<Record<string, unknown>>,
          {
            id: fieldId,
            'aria-describedby': described,
            'aria-labelledby': labelledBy
          } as Record<string, unknown>
        );
      })
    : null;
  const labelElement = label ? (
    <Label
      id={labelId}
      htmlFor={fieldId}
      className={cn('break-words whitespace-normal', {
        'text-destructive': errors.length > 0,
        'sr-only': isLabelVisuallyHidden
      })}
    >
      {label}
      {isRequired ? (
        <span aria-hidden="true" className="ml-0.5 text-destructive/70 select-none">
          *
        </span>
      ) : null}
    </Label>
  ) : null;
  const helpTextElement = helpText ? (
    <div
      id={helpTextId}
      className={cn('text-xs break-words whitespace-pre-wrap text-muted-foreground', {
        'col-start-2': isInline,
        'sr-only': isHelpTextVisuallyHidden
      })}
    >
      {helpText}
    </div>
  ) : null;

  return (
    <div ref={ref} className={cn('w-full', classes)} {...props}>
      <div
        className={
          isInline
            ? cn(
                'grid gap-x-2',
                inlineActions ? 'grid-cols-[auto_1fr_auto]' : 'grid-cols-[auto_1fr]'
              )
            : 'flex flex-col gap-1.5'
        }
      >
        {labelElement && !labelActions && isLabelVisuallyHidden ? (
          // Nothing shares the row, so it collapses entirely instead of leaving
          // the gap the label would have occupied.
          labelElement
        ) : labelElement || labelActions ? (
          <div
            className={cn('flex min-w-0 items-center justify-between gap-2', {
              'col-start-2': isInline
            })}
          >
            <div className="min-w-0 flex-1">{labelElement}</div>
            {labelActions ? <div className="shrink-0">{labelActions}</div> : null}
          </div>
        ) : null}
        {helpTextPlacement === 'before-control' ? helpTextElement : null}
        {clonedChildren || stackedActions ? (
          <div
            className={cn('flex min-w-0 gap-2', {
              'col-start-1 row-span-2 row-start-1 items-start pt-1': isInline,
              'items-center': !isInline
            })}
          >
            {clonedChildren ? (
              <div className="min-w-0 flex-1">
                {/* A hidden label applies to this field alone — a composite
                    editor nested inside still names its own sub-fields. */}
                <FormFieldLabelVisibilityContext.Provider value="visible">
                  <FormFieldHelpTextVisibilityContext.Provider value="visible">
                    {clonedChildren}
                  </FormFieldHelpTextVisibilityContext.Provider>
                </FormFieldLabelVisibilityContext.Provider>
              </div>
            ) : null}
            {stackedActions ? <div className="shrink-0">{stackedActions}</div> : null}
          </div>
        ) : null}
        {inlineActions ? (
          <div className="col-start-3 row-start-1 shrink-0">{inlineActions}</div>
        ) : null}
        {helpTextPlacement === 'after-control' ? helpTextElement : null}
        {errors.length > 0 ? (
          <div
            id={errorId}
            className={cn('text-xs text-destructive', {
              'col-start-2': isInline
            })}
          >
            {errors.map((error) => (
              <p key={error}>{error}</p>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};
FormField.displayName = 'FormField';
